import type {
  AssetHazardInstance,
  VulnerabilityData,
} from '@app/components/Molecules/MapView/RiskMapView/RiskMapView'
import type { RegionOption } from '@app/components/Molecules/RegionFilter'
import type { DrawArea, MapLayer } from '@app/types/layerTypes'
import type { IdentifyingInformationFilter } from '@app/types/filterTypes'
import { useQueries } from '@tanstack/react-query'
import { getVulnerabilityStats } from '../getVulnerabilityStats'

interface UseVulnerabilityStatsProps {
  assetLayers: MapLayer[]
  hazardLayers: MapLayer[]
  regionMasks: RegionOption[] | null
  drawAreas: DrawArea[]
  elementFilters: IdentifyingInformationFilter
  cascadingAssetNDays: Record<string, number | null>
  clientName: string | null
  enabled: boolean
}

type VulnerabilityStatsByAssetId = Record<string, AssetHazardInstance>

export function useVulnerabilityStats({
  assetLayers,
  hazardLayers,
  drawAreas,
  regionMasks,
  elementFilters,
  cascadingAssetNDays,
  clientName,
  enabled,
}: UseVulnerabilityStatsProps) {
  const regions = regionMasks?.map((region) => region.region).join(',') ?? ''

  return useQueries({
    queries: assetLayers.flatMap((assetLayer) =>
      hazardLayers.map((hazardLayer) => {
        const nDays = assetLayer.is_cascading ? (cascadingAssetNDays[assetLayer.type] ?? 0) : 0

        return {
          queryKey: [
            'vulnerability',
            clientName,
            assetLayer.type,
            hazardLayer.assetTag,
            regions,
            drawAreas,
            nDays,
            elementFilters[assetLayer.type],
          ],

          queryFn: async (): Promise<VulnerabilityStatsByAssetId> =>
            getVulnerabilityStats({
              assetLayer,
              hazardLayer,
              regionMasks,
              drawAreas,
              elementFilters,
              cascadingAssetNDays,
            }),

          enabled,
          staleTime: Infinity,
        }
      }),
    ),

    combine: (results) => {
      const data: VulnerabilityData = {}

      let index = 0

      assetLayers.forEach((assetLayer) => {
        data[assetLayer.type] ??= {}

        hazardLayers.forEach((hazardLayer) => {
          const result = results[index++]

          if (result.data) {
            data[assetLayer.type][hazardLayer.assetTag] = result.data
          }
        })
      })

      return {
        data,
        isLoading: results.some((result) => result.isLoading),
        isFetching: results.some((result) => result.isFetching),
        isError: results.some((result) => result.isError),
      }
    },
  })
}
